import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys } from '../api/queryKeys'
import type {
  PhysicalMailListResponse,
  PhysicalMailRow,
} from '../api/types'
import { CodeChip } from '../components/CodeChip'
import {
  DataTable, type ColumnConfig,
} from '../components/DataTable'
import { PaginationControl } from '../components/PaginationControl'
import { RelativeTime } from '../components/RelativeTime'
import { StatusBadge } from '../components/StatusBadge'
import { useCursorPaginatedQuery } from '../hooks/useCursorPaginatedQuery'

// plan-admin/08 — Physical Mail dispatch queue. Lists every message
// whose `physical_address.dispatch_status` is in
// PENDING / FAILED (and DISPATCHED when the filter chip is on); the
// row click takes the operator to the per-message dispatch page.
//
// VIEWER-readable: the list never reveals the encrypted address, only
// `country` (intentionally stored in plaintext for routing — see
// plan-admin/08 §"Country in plaintext").
//
// review_comments M2 (HEAD 0f55588): the endpoint is cursor-paginated;
// the SPA uses `useCursorPaginatedQuery` and a `PaginationControl`
// footer so a queue spike cannot DOS the SPA.

const PAGE_SIZE = 50

export function PhysicalMailListPage() {
  const client = useAdminApiClient()
  const navigate = useNavigate()
  const [includeDispatched, setIncludeDispatched] = useState(false)

  const result = useCursorPaginatedQuery<PhysicalMailRow>({
    queryKey: queryKeys.physicalMail({ includeDispatched }),
    fetchPage: (cursor) => {
      const search = new URLSearchParams({ limit: String(PAGE_SIZE) })
      if (cursor) search.set('cursor', cursor)
      if (includeDispatched) search.set('include_dispatched', 'true')
      return client.get<PhysicalMailListResponse>(
        `/r/admin/physical-mail?${search.toString()}`,
      )
    },
  })

  const rows = result.flatItems

  const columns: ColumnConfig<PhysicalMailRow>[] = [
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <StatusBadge kind="physical_dispatch" value={row.dispatch_status} />
      ),
    },
    {
      key: 'recipient_name',
      header: 'Recipient',
      cell: (row) =>
        row.recipient_name || <span className="text-stone-400">—</span>,
    },
    {
      key: 'sender_display_name',
      header: 'Sender',
      cell: (row) =>
        row.sender_display_name
          || <span className="text-stone-400">—</span>,
    },
    {
      key: 'country',
      header: 'Country',
      cell: (row) => (
        <CodeChip>{row.country || '??'}</CodeChip>
      ),
    },
    {
      key: 'tracking_number',
      header: 'Tracking',
      cell: (row) => row.tracking_number
        ? <CodeChip>{row.tracking_number}</CodeChip>
        : <span className="text-xs text-stone-400">—</span>,
    },
    {
      key: 'dispatched_time',
      header: 'Dispatched',
      cell: (row) => row.dispatched_time
        ? <RelativeTime timestamp={row.dispatched_time} />
        : <span className="text-xs text-stone-400">—</span>,
    },
  ]

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">
            Physical mail dispatch
          </h1>
          <p className="mt-1 text-sm text-stone-500">
            Letters awaiting dispatch by the operator. Begin dispatch
            on a row to reveal the recipient address (audited).
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input
            type="checkbox"
            checked={includeDispatched}
            onChange={(e) => setIncludeDispatched(e.target.checked)}
            className="h-4 w-4 rounded border-stone-300"
          />
          Include posted (DISPATCHED)
        </label>
      </header>
      <DataTable<PhysicalMailRow>
        caption="Physical mail dispatch queue"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.message_id}
        isLoading={result.isLoading}
        isError={result.isError}
        errorMessage={result.error?.message ?? 'Failed to load queue.'}
        emptyTitle="No letters waiting to be dispatched"
        emptyDescription={
          includeDispatched
            ? 'Nothing pending or already posted.'
            : 'Letters waiting on the postal service will appear here.'
        }
        onRowClick={(row) =>
          navigate(`/physical-mail/${row.message_id}`)
        }
        footer={
          <PaginationControl
            loadedCount={rows.length}
            hasMore={result.hasMore}
            isFetchingMore={result.isFetchingMore}
            onLoadMore={() => result.loadMore()}
          />
        }
      />
    </div>
  )
}
